import { useEffect, useMemo } from "react";

import { useLocation, useParams } from "react-router-dom";

import { useLanguage } from "@/context/LanguageContext";

import { useSiteContent } from "@/context/SiteContentContext";

import { resolvePageSeo, buildJsonLd, absoluteCanonical } from "@/lib/seoConfig";

import { ROUTES, normalizeLegacyPath } from "@/lib/routes";



function upsertMeta(attr, key, content) {

  if (!content) return;

  let el = document.querySelector(`meta[${attr}="${key}"]`);

  if (!el) {

    el = document.createElement("meta");

    el.setAttribute(attr, key);

    document.head.appendChild(el);

  }

  el.setAttribute("content", content);

}



function upsertLink(rel, href) {

  if (!href) return;

  let el = document.querySelector(`link[rel="${rel}"]`);

  if (!el) {

    el = document.createElement("link");

    el.setAttribute("rel", rel);

    document.head.appendChild(el);

  }

  el.setAttribute("href", href);

}



function absoluteUrl(path) {

  if (!path) return "";

  if (/^https?:\/\//i.test(path)) return path;

  const origin = typeof window !== "undefined" ? window.location.origin : "https://socialdestiny.ro";

  return `${origin}${path.startsWith("/") ? path : `/${path}`}`;

}



const DEFAULT_SEO = {

  ro: {

    siteTitle: "Social Destiny - Agenție Social Media",

    metaDescription:

      "Tot ce vrei de la o agenție de marketing predictibilă: content & producție media, website & SEO, ads & performance, brand & strategy. Rezultate reale, raportate transparent.",

    shareTitle: "",

    shareDescription: "",

    shareImageUrl: "/logo_sd.png",

  },

  en: {

    siteTitle: "Social Destiny - Social Media Agency",

    metaDescription:

      "Everything you want from a predictable marketing agency: content & media production, websites & SEO, ads & performance, brand & strategy. Real results, clearly reported.",

    shareTitle: "",

    shareDescription: "",

    shareImageUrl: "/logo_sd.png",

  },

};



const JSONLD_ID = "sd-page-jsonld";



export default function SeoHead() {

  const { lang, branding, t, contactInfo } = useLanguage();

  const { getMergedServicePage } = useSiteContent();

  const location = useLocation();

  const params = useParams();



  const brandingSeo = useMemo(() => {

    const fromBranding = branding?.seo?.[lang] || branding?.seo?.ro || {};

    const defaults = DEFAULT_SEO[lang] || DEFAULT_SEO.ro;

    return { ...defaults, ...fromBranding };

  }, [branding, lang]);



  const pathname = normalizeLegacyPath(location.pathname);

  const slug = params.slug;

  const serviceData = slug ? getMergedServicePage(lang, slug) : null;



  const pageSeo = useMemo(

    () => resolvePageSeo(pathname, params, lang, t, serviceData, brandingSeo),

    [pathname, params, lang, t, serviceData, brandingSeo]

  );



  useEffect(() => {

    const shareTitle = pageSeo.title || brandingSeo.siteTitle;

    const shareDescription = pageSeo.description || brandingSeo.metaDescription;

    const shareImage = absoluteUrl(brandingSeo.shareImageUrl || "/logo_sd.png");

    const canonicalPath = pageSeo.path || pathname || ROUTES.home;

    const canonicalUrl = absoluteCanonical(canonicalPath);



    document.title = shareTitle;

    document.documentElement.lang = lang;



    upsertMeta("name", "description", shareDescription);

    upsertMeta("name", "robots", "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1");

    upsertLink("canonical", canonicalUrl);



    upsertMeta("property", "og:title", shareTitle);

    upsertMeta("property", "og:description", shareDescription);

    upsertMeta("property", "og:image", shareImage);

    upsertMeta("property", "og:url", canonicalUrl);

    upsertMeta("property", "og:type", "website");

    upsertMeta("property", "og:site_name", "Social Destiny");

    upsertMeta("property", "og:locale", lang === "en" ? "en_US" : "ro_RO");



    upsertMeta("name", "twitter:card", "summary_large_image");

    upsertMeta("name", "twitter:title", shareTitle);

    upsertMeta("name", "twitter:description", shareDescription);

    upsertMeta("name", "twitter:image", shareImage);



    const jsonLd = buildJsonLd({

      pathname: canonicalPath,

      lang,

      t,

      serviceData,

      contactInfo,

      pageSeo: { ...pageSeo, title: shareTitle, description: shareDescription },

    });



    let script = document.getElementById(JSONLD_ID);
    document.getElementById("sd-schema-org")?.remove();

    if (!script) {

      script = document.createElement("script");

      script.id = JSONLD_ID;

      script.type = "application/ld+json";

      document.head.appendChild(script);

    }

    script.textContent = JSON.stringify(jsonLd);

  }, [pageSeo, pathname, lang, brandingSeo, t, serviceData, contactInfo]);



  return null;

}

